// The browser half of a passkey ceremony (#636, D10).
//
// `passkeys.ts` is the HTTP side; this is `navigator.credentials.get` and
// `.create`, and the base64url bridging between the DOM's buffers and the
// server's JSON. It is shared by the login challenge (`SignIn.tsx`), the step-up
// dialog (`StepUp.tsx`) and the enrolment in Settings (`Passkeys.tsx`, #660), so
// an assertion and an attestation cannot drift in how they are packaged.
//
// **The getter is a parameter, not a global.** A test supplies a fake and
// inspects exactly what would reach `/auth/mfa/complete` or `/auth/step-up`,
// with no DOM and no browser — the same seam the rest of the console tests at.

import type {
  AttestationJson,
  PasskeyChallenge,
  PasskeyCreationChallenge,
  PasskeyDescriptor,
} from "../api/passkeys";

/** The assertion as the server's JSON: base64url strings, never buffers. */
export interface AssertionJson {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

/** `navigator.credentials.get`, injectable so a test needs no browser. */
export type CredentialGetter = (options: CredentialRequestOptions) => Promise<Credential | null>;

/** `navigator.credentials.create`, injectable for the same reason (#660). */
export type CredentialCreator = (options: CredentialCreationOptions) => Promise<Credential | null>;

export function base64urlToBytes(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return buffer;
}

export function bytesToBase64url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function assertionJson(credential: PublicKeyCredential): AssertionJson {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64url(response.clientDataJSON),
      authenticatorData: bytesToBase64url(response.authenticatorData),
      signature: bytesToBase64url(response.signature),
      userHandle: response.userHandle ? bytesToBase64url(response.userHandle) : null,
    },
  };
}

/**
 * A browser answers a cancelled or timed-out prompt by rejecting with
 * `NotAllowedError`, not by resolving null; both are a dismissal. Any other
 * failure is not, and is raised.
 */
async function unlessDismissed(prompt: Promise<Credential | null>): Promise<Credential | null> {
  try {
    return await prompt;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotAllowedError") return null;
    throw error;
  }
}

/** A credential the server named, as the DOM takes it: the id as a buffer. */
function descriptors(entries: PasskeyDescriptor[] | undefined): PublicKeyCredentialDescriptor[] {
  return (entries ?? []).map((entry) => ({
    type: "public-key" as PublicKeyCredentialType,
    id: base64urlToBytes(entry.id),
    transports: entry.transports as AuthenticatorTransport[] | undefined,
  }));
}

/** The `CredentialRequestOptions` the server's challenge asks the browser for. */
export function assertionRequest(challenge: PasskeyChallenge): CredentialRequestOptions {
  return {
    publicKey: {
      challenge: base64urlToBytes(challenge.public_key.challenge),
      rpId: challenge.public_key.rpId,
      timeout: challenge.public_key.timeout,
      userVerification:
        (challenge.public_key.userVerification as UserVerificationRequirement | undefined) ??
        "required",
      allowCredentials: descriptors(challenge.public_key.allowCredentials),
    },
  };
}

/** The `CredentialCreationOptions` the server's registration options ask for (#660). */
export function creationRequest(challenge: PasskeyCreationChallenge): CredentialCreationOptions {
  const key = challenge.public_key;
  const selection = key.authenticatorSelection ?? {};
  return {
    publicKey: {
      challenge: base64urlToBytes(key.challenge),
      rp: { id: key.rp.id, name: key.rp.name },
      user: {
        id: base64urlToBytes(key.user.id),
        name: key.user.name,
        displayName: key.user.displayName,
      },
      pubKeyCredParams: key.pubKeyCredParams.map((param) => ({
        type: "public-key" as PublicKeyCredentialType,
        alg: param.alg,
      })),
      timeout: key.timeout,
      attestation: (key.attestation as AttestationConveyancePreference | undefined) ?? "none",
      authenticatorSelection: {
        residentKey: selection.residentKey as ResidentKeyRequirement | undefined,
        userVerification:
          (selection.userVerification as UserVerificationRequirement | undefined) ?? "required",
      },
      excludeCredentials: descriptors(key.excludeCredentials),
    },
  };
}

export function attestationJson(credential: PublicKeyCredential): AttestationJson {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64url(response.clientDataJSON),
      attestationObject: bytesToBase64url(response.attestationObject),
    },
    transports:
      typeof response.getTransports === "function" ? response.getTransports() : [],
  };
}

/**
 * Run the create prompt for the server's registration options and return what
 * `POST /auth/passkeys/register/finish` takes besides the name and the grant, or
 * `null` if the person dismissed the prompt.
 */
export async function passkeyAttestation(
  challenge: PasskeyCreationChallenge,
  create: CredentialCreator = (options) => navigator.credentials.create(options),
): Promise<{ ceremony_token: string; credential: AttestationJson } | null> {
  const created = (await unlessDismissed(
    create(creationRequest(challenge)),
  )) as PublicKeyCredential | null;
  if (created === null) return null;
  return { ceremony_token: challenge.ceremony_token, credential: attestationJson(created) };
}

/**
 * Run the assertion ceremony and return the credential the server wants, or
 * `null` if the person dismissed the prompt.
 *
 * The JSON is `{ceremony_token, assertion}`: the token ties the answer to the
 * challenge this server minted, and the assertion is the proof. Both
 * `/auth/mfa/complete` (a login) and `/auth/step-up` (a fresh factor inside a
 * session) take exactly this shape for `method: "passkey"`.
 */
export async function passkeyCredential(
  challenge: PasskeyChallenge,
  get: CredentialGetter = (options) => navigator.credentials.get(options),
): Promise<string | null> {
  const assertion = (await unlessDismissed(
    get(assertionRequest(challenge)),
  )) as PublicKeyCredential | null;
  if (assertion === null) return null;
  return JSON.stringify({
    ceremony_token: challenge.ceremony_token,
    assertion: assertionJson(assertion),
  });
}

/**
 * The login ceremony end to end: mint a challenge for `mfaToken`, answer it in
 * the browser, and hand back the body `POST /auth/mfa/complete` takes. `null`
 * means the ceremony was dismissed.
 */
export async function passkeyLoginCompletion(
  begin: (mfaToken: string) => Promise<PasskeyChallenge>,
  mfaToken: string,
  get?: CredentialGetter,
): Promise<{ mfa_token: string; method: string; credential: string } | null> {
  const began = await begin(mfaToken);
  const credential = await passkeyCredential(began, get);
  if (credential === null) return null;
  return { mfa_token: mfaToken, method: "passkey", credential };
}
